import { useEffect, useId, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Box, Button, IconButton, VStack } from '@chakra-ui/react';
import { Plus } from 'lucide-react';

export interface PlusMenuItem {
  key: string;
  label: string;
  icon: ReactElement;
  onSelect: () => void;
}

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.closest('input, textarea, select, [contenteditable="true"]') !==
    null ||
    target.isContentEditable);

/**
 * The composer's "+" (D272): a small panel anchored above the composer, not
 * a modal. Neither the button nor the items take focus from a tap, so the
 * text field keeps it and the phone keyboard stays where it was (up stays
 * up, down stays down). A tap outside, "+" again or Esc closes it; each
 * action decides for itself whether it takes over the screen and lets the
 * keyboard go. Opened from the keyboard (Enter/Space on "+"), focus moves
 * into the items for arrow keys, and Esc gives it back to "+".
 */
export function ComposerPlusMenu({
  label,
  items,
}: {
  label: string;
  items: PlusMenuItem[];
}) {
  const [open, setOpen] = useState(false);
  const fromKeyboard = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const itemNodes = () =>
    Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
    );

  const close = (refocusButton: boolean) => {
    setOpen(false);
    if (refocusButton) buttonRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    if (fromKeyboard.current) itemNodes()[0]?.focus();
    const inside = (target: EventTarget | null) =>
      target instanceof Node &&
      (panelRef.current?.contains(target) ||
        buttonRef.current?.contains(target));
    // A tap outside closes it. Unless it lands where people type, the tap
    // doesn't move focus either, so the keyboard stays as it was.
    const onDown = (event: MouseEvent) => {
      if (inside(event.target)) return;
      if (!isTypingTarget(event.target)) event.preventDefault();
      setOpen(false);
    };
    // Esc closes the menu before anything else hears it (Esc in the text
    // field would otherwise drop a reply or an edit).
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      const focusInMenu = panelRef.current?.contains(document.activeElement);
      close(Boolean(focusInMenu));
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const onItemKeyDown = (event: React.KeyboardEvent) => {
    const nodes = itemNodes();
    const index = nodes.indexOf(document.activeElement as HTMLElement);
    const move = (to: number) => {
      event.preventDefault();
      nodes[(to + nodes.length) % nodes.length]?.focus();
    };
    if (event.key === 'ArrowDown') move(index + 1);
    else if (event.key === 'ArrowUp') move(index - 1);
    else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(nodes.length - 1);
    else if (event.key === 'Tab') setOpen(false);
  };

  return (
    // The panel is placed against the composer's pill (its nearest
    // positioned box), so it sits above the whole pill however tall the
    // text grows.
    <Box flexShrink={0}>
      <IconButton
        ref={buttonRef}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid="plus-button"
        icon={<Plus size={22} />}
        variant="ghost"
        bg="accent.tint"
        color="accent"
        _hover={{ bg: 'brand.100' }}
        _dark={{ _hover: { bg: 'brand.700' } }}
        // A tap doesn't take focus: the text field keeps it (D272).
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => {
          // detail 0: Enter or Space, not a pointer.
          fromKeyboard.current = event.detail === 0;
          setOpen((was) => !was);
        }}
      />
      {open && (
        <Box
          ref={panelRef}
          id={menuId}
          role="menu"
          aria-label={label}
          data-testid="plus-menu"
          position="absolute"
          bottom="100%"
          insetStart={0}
          mb={2}
          minW="220px"
          maxW="calc(100vw - 32px)"
          zIndex="popover"
          bg="surface.raised"
          border="1px solid"
          borderColor="line"
          borderRadius="xl"
          boxShadow="lg"
          py={2}
          onKeyDown={onItemKeyDown}
        >
          <VStack spacing={0} align="stretch">
            {items.map((item) => (
              <Button
                key={item.key}
                role="menuitem"
                tabIndex={-1}
                variant="ghost"
                color="ink"
                justifyContent="flex-start"
                leftIcon={item.icon}
                iconSpacing={3}
                fontWeight={400}
                fontSize="md"
                h="44px"
                px={4}
                borderRadius={0}
                _hover={{ bg: 'surface.subtle' }}
                _focus={{ bg: 'surface.subtle' }}
                // Picking doesn't move focus either; the action decides.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                {item.label}
              </Button>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}
